import React, { useEffect, useRef, useState } from 'react';
import { webLoadingFalse } from '../../services/navigate.service';
import { useResponsive } from '../../hook/useResponsive';
import DeviceIcon from '../../images/DeviceIcon';
import { getDeviceHealthList as fetchDeviceHealthList } from '../../services/deviceHealth.service';
import {
  BottomSheet,
  BORDER_SOFT,
  CHIP_TEAL_BG,
  ListColumn,
  ListFilter,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  TEAL,
  TEXT,
  TEXT_DIM,
  TEXT_MUTED,
  cells,
  t,
} from '../../components/list';

interface DeviceHealthRow {
  id: string;
  deviceName?: string | null;
  appVersion?: string | null;
  lastSeenAt?: string | null;
  networkStatus?: string | null;
  pendingCount?: number | null;
  failedCount?: number | null;
  failedDependencyCount?: number | null;
  oldestQueueItemSeconds?: number | null;
  printerConnected?: boolean | null;
  lastError?: string | null;
  updatedAt?: string | null;
  device?: {
    id: string;
    deviceName?: string | null;
    serialNo?: string | null;
  } | null;
  location?: {
    id: string;
    locationCode?: string | null;
    name?: string | null;
  } | null;
  company?: {
    id: string;
    companyName?: string | null;
  } | null;
}

/* ---------------- Health model ---------------- */

type HealthStatus = 'attention' | 'backlog' | 'healthy';

/** A till that has not beaten for this long is treated as not reporting. */
const STALE_AFTER_MS = 15 * 60 * 1000;

const HEALTH_STYLE: Record<
  HealthStatus,
  { bg: string; fg: string; label: string }
> = {
  attention: { bg: '#F6E0E0', fg: '#BA0E13', label: 'Needs attention' },
  backlog: { bg: '#F8EFD4', fg: '#7A5D12', label: 'Backlog' },
  healthy: { bg: '#E4F1EA', fg: '#1F6B4A', label: 'Healthy' },
};

const lastSeenOf = (row: DeviceHealthRow) =>
  row.lastSeenAt ?? row.updatedAt ?? null;

const isStale = (row: DeviceHealthRow) => {
  const ts = lastSeenOf(row);
  if (!ts) return true;
  const time = new Date(ts).getTime();
  return Number.isNaN(time) || Date.now() - time > STALE_AFTER_MS;
};

const isOffline = (row: DeviceHealthRow) =>
  (row.networkStatus ?? '').toUpperCase() !== 'ONLINE';

const healthOf = (row: DeviceHealthRow): HealthStatus => {
  const failed = (row.failedCount ?? 0) + (row.failedDependencyCount ?? 0);
  if (
    failed > 0 ||
    isOffline(row) ||
    isStale(row) ||
    row.printerConnected === false
  )
    return 'attention';
  return (row.pendingCount ?? 0) > 0 ? 'backlog' : 'healthy';
};

/** Why a row needs attention / has a backlog — at most two reasons, worst first. */
const reasonsOf = (row: DeviceHealthRow, language: any): string => {
  const reasons: string[] = [];
  const failed = row.failedCount ?? 0;
  const dependencyFailed = row.failedDependencyCount ?? 0;
  if (failed > 0)
    reasons.push(`${failed} ${t(language, 'failed sync', 'failed sync')}`);
  if (dependencyFailed > 0)
    reasons.push(
      `${dependencyFailed} ${t(language, 'failed refunds', 'failed refunds')}`,
    );
  if (isStale(row)) reasons.push(t(language, 'Not reporting', 'Not reporting'));
  else if (isOffline(row)) reasons.push(t(language, 'Offline', 'Offline'));
  if (row.printerConnected === false)
    reasons.push(t(language, 'Printer disconnected', 'Printer disconnected'));
  if (reasons.length === 0 && (row.pendingCount ?? 0) > 0)
    reasons.push(
      `${row.pendingCount} ${t(language, 'waiting to sync', 'waiting to sync')}`,
    );
  return reasons.slice(0, 2).join(' · ');
};

const deviceLabelOf = (row: DeviceHealthRow) =>
  row.deviceName ?? row.device?.deviceName ?? row.device?.serialNo ?? '—';

/* ---------------- Formatting ---------------- */

const formatExact = (dateString: any) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatAgo = (dateString: any, language: any) => {
  if (!dateString) return t(language, 'Never', 'Never');
  const time = new Date(dateString).getTime();
  if (Number.isNaN(time)) return '-';
  const seconds = Math.max(0, Math.floor((Date.now() - time) / 1000));
  if (seconds < 60) return t(language, 'Just now', 'Just now');
  if (seconds < 3600)
    return `${Math.floor(seconds / 60)}m ${t(language, 'ago', 'ago')}`;
  if (seconds < 86400)
    return `${Math.floor(seconds / 3600)}h ${t(language, 'ago', 'ago')}`;
  return `${Math.floor(seconds / 86400)}d ${t(language, 'ago', 'ago')}`;
};

const formatQueueAge = (seconds?: number | null) => {
  if (seconds === null || seconds === undefined || seconds <= 0) return '-';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
};

/* ---------------- Small pieces ---------------- */

const HealthPill: React.FC<{ status: HealthStatus; language: any }> = ({
  status,
  language,
}) => {
  const style = HEALTH_STYLE[status];
  return (
    <span
      className="font-inter"
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '999px',
        backgroundColor: style.bg,
        color: style.fg,
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: '18px',
        whiteSpace: 'nowrap',
      }}
    >
      {t(language, style.label, style.label)}
    </span>
  );
};

interface FleetSummary {
  attention: number;
  backlog: number;
  healthy: number;
  refreshedAt: Date;
}

const DeviceHealth = () => {
  const [language, setLanguage] = useState<any>(null);
  const { isMobile } = useResponsive();
  const listRef = useRef<ListScreenHandle>(null);
  const [summary, setSummary] = useState<FleetSummary | null>(null);
  const [statusFilter, setStatusFilter] = useState<HealthStatus | ''>('');
  const statusFilterRef = useRef(statusFilter);
  statusFilterRef.current = statusFilter;
  const [selected, setSelected] = useState<DeviceHealthRow | null>(null);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const cacheRef = useRef<{
    key: string;
    at: number;
    rows: DeviceHealthRow[];
  } | null>(null);

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  // A quiet minute-by-minute refresh; paused while the detail sheet is open.
  useEffect(() => {
    const timer = setInterval(() => {
      if (selectedRef.current) return;
      cacheRef.current = null;
      listRef.current?.reload();
    }, 60_000);
    return () => clearInterval(timer);
  }, []);

  const inforTable: any = {
    img: <DeviceIcon />,
    title: t(language, 'No devices yet', 'No devices yet'),
    path: '/deviceHealth/',
    desc: t(
      language,
      'Tills report their health every few minutes. A device appears here after its first report.',
      'Tills report their health every few minutes. A device appears here after its first report.',
    ),
  };

  /**
   * The endpoint pages but cannot filter by status or sort by severity, so the
   * screen loads the fleet once (cached briefly) and slices client-side.
   */
  const loadFleet = async (filters: Record<string, string>) => {
    const key = JSON.stringify([
      filters.searchDevice ?? '',
      filters.searchCompany ?? '',
      filters.searchLocation ?? '',
    ]);
    const now = Date.now();
    if (
      cacheRef.current &&
      cacheRef.current.key === key &&
      now - cacheRef.current.at < 25_000
    ) {
      return cacheRef.current.rows;
    }
    const rows: DeviceHealthRow[] = [];
    let page = 1;
    for (;;) {
      const response: any = await fetchDeviceHealthList({
        page,
        limit: 500,
        searchDevice: filters.searchDevice ?? '',
        searchLocation: filters.searchLocation ?? '',
        searchCompany: filters.searchCompany ?? '',
        searchDate: '',
      });
      const batch: DeviceHealthRow[] = response.data.data.deviceHealths ?? [];
      rows.push(...batch);
      const count = response.data.data.count ?? rows.length;
      if (batch.length === 0 || rows.length >= count || page >= 4) break;
      page += 1;
    }
    cacheRef.current = { key, at: now, rows };
    return rows;
  };

  const severityRank: Record<HealthStatus, number> = {
    attention: 0,
    backlog: 1,
    healthy: 2,
  };

  const fetchHealth = async (query: ListQuery) => {
    webLoadingFalse();
    const fleet = await loadFleet(query.filters);

    const counts = { attention: 0, backlog: 0, healthy: 0 };
    for (const row of fleet) counts[healthOf(row)] += 1;
    setSummary({ ...counts, refreshedAt: new Date() });

    const status = statusFilterRef.current;
    let rows = status
      ? fleet.filter((row) => healthOf(row) === status)
      : [...fleet];

    const dir = query.sortDir === 'ASC' ? 1 : -1;
    const seen = (row: DeviceHealthRow) =>
      new Date(lastSeenOf(row) ?? 0).getTime() || 0;
    if (query.sortBy === 'device') {
      rows.sort(
        (a, b) => dir * deviceLabelOf(a).localeCompare(deviceLabelOf(b)),
      );
    } else if (query.sortBy === 'lastSeen') {
      rows.sort((a, b) => dir * (seen(a) - seen(b)));
    } else if (query.sortBy === 'pendingCount') {
      rows.sort(
        (a, b) => dir * ((a.pendingCount ?? 0) - (b.pendingCount ?? 0)),
      );
    } else if (query.sortBy === 'failedCount') {
      rows.sort((a, b) => dir * ((a.failedCount ?? 0) - (b.failedCount ?? 0)));
    } else {
      // Default: worst first, and the longest-silent inside each band.
      rows.sort(
        (a, b) =>
          severityRank[healthOf(a)] - severityRank[healthOf(b)] ||
          seen(a) - seen(b),
      );
    }

    const start = (query.page - 1) * query.limit;
    return { rows: rows.slice(start, start + query.limit), count: rows.length };
  };

  const applyStatusFilter = (status: HealthStatus | '') => {
    setStatusFilter((current) => (current === status ? '' : status));
    // The ref updates on render; reload on the next tick so the fetch sees it.
    setTimeout(() => listRef.current?.reload(), 0);
  };

  /* ---------------- Summary strip ---------------- */

  const summaryTiles: { status: HealthStatus; count: number }[] = summary
    ? [
        { status: 'attention', count: summary.attention },
        { status: 'backlog', count: summary.backlog },
        { status: 'healthy', count: summary.healthy },
      ]
    : [];

  const summaryStrip = summary && (
    <div
      className="flex items-center flex-wrap"
      style={{ gap: '8px', marginTop: '12px' }}
    >
      {summaryTiles.map((tile) => {
        const style = HEALTH_STYLE[tile.status];
        const active = statusFilter === tile.status;
        return (
          <button
            key={tile.status}
            className="font-inter flex items-center"
            style={{
              gap: '8px',
              padding: '8px 14px',
              borderRadius: '10px',
              border: `1px solid ${active ? TEAL : BORDER_SOFT}`,
              backgroundColor: active ? CHIP_TEAL_BG : '#FFFFFF',
              cursor: 'pointer',
            }}
            aria-pressed={active}
            onClick={() => applyStatusFilter(tile.status)}
          >
            <span
              aria-hidden
              style={{
                width: '9px',
                height: '9px',
                borderRadius: '100%',
                backgroundColor: style.fg,
                flex: 'none',
              }}
            />
            <span style={{ fontSize: '13px', color: TEXT_MUTED }}>
              {t(language, style.label, style.label)}
            </span>
            <span style={{ fontSize: '14px', fontWeight: 600, color: TEXT }}>
              {tile.count}
            </span>
          </button>
        );
      })}
      <span
        className="font-inter"
        style={{ fontSize: '12px', color: TEXT_DIM, marginLeft: '4px' }}
      >
        {t(language, 'Updated', 'Updated')}{' '}
        {formatAgo(summary.refreshedAt, language)}
      </span>
      <button
        className="font-inter"
        style={{
          fontSize: '13px',
          fontWeight: 500,
          color: TEAL,
          padding: '4px 6px',
        }}
        onClick={() => {
          cacheRef.current = null;
          listRef.current?.reload();
        }}
      >
        {t(language, 'Refresh', 'Refresh')}
      </button>
    </div>
  );

  /* ---------------- List kit configuration ---------------- */

  const filters: ListFilter[] = [
    { key: 'searchDevice', label: 'Device', kind: 'text' },
    { key: 'searchCompany', label: 'Company', kind: 'text' },
    { key: 'searchLocation', label: 'Location', kind: 'text' },
  ];

  const columns: ListColumn<DeviceHealthRow>[] = [
    {
      key: 'device',
      label: 'Device',
      width: 170,
      minWidth: 120,
      hideable: false,
      sortable: true,
      value: (row) => deviceLabelOf(row),
      render: cells.primary(
        (row) => deviceLabelOf(row),
        (row) => row.device?.serialNo ?? undefined,
      ),
      onClick: (row) => setSelected(row),
      mobile: 'title',
    },
    {
      key: 'status',
      label: 'Status',
      width: 200,
      minWidth: 150,
      value: (row, ctx) =>
        t(
          ctx.language,
          HEALTH_STYLE[healthOf(row)].label,
          HEALTH_STYLE[healthOf(row)].label,
        ),
      render: (row) => (
        <div>
          <HealthPill status={healthOf(row)} language={language} />
          {reasonsOf(row, language) && (
            <div
              className="font-inter"
              style={{ fontSize: '12px', color: TEXT_MUTED, marginTop: '3px' }}
            >
              {reasonsOf(row, language)}
            </div>
          )}
        </div>
      ),
      mobileRender: (row) => (
        <HealthPill status={healthOf(row)} language={language} />
      ),
      mobile: 'trailing',
    },
    {
      key: 'company',
      label: 'Company',
      width: 140,
      minWidth: 100,
      value: (row) => row.company?.companyName ?? '-',
      mobile: 'subtitle',
    },
    {
      key: 'location',
      label: 'Location',
      width: 130,
      minWidth: 96,
      value: (row) => row.location?.name ?? '-',
      mobile: 'subtitle',
    },
    {
      key: 'lastSeen',
      label: 'Last Seen',
      width: 110,
      minWidth: 92,
      sortable: true,
      value: (row) => formatAgo(lastSeenOf(row), language),
      render: (row) => (
        <div
          className="font-inter"
          style={{
            fontSize: '13px',
            color: isStale(row) ? HEALTH_STYLE.attention.fg : TEXT_MUTED,
          }}
        >
          {formatAgo(lastSeenOf(row), language)}
        </div>
      ),
      mobile: 'sheet',
    },
    {
      key: 'pendingCount',
      label: 'Pending Sync',
      width: 100,
      minWidth: 84,
      align: 'right',
      sortable: true,
      value: (row) => String(row.pendingCount ?? 0),
      render: (row) => (
        <div
          className="font-inter"
          style={{
            fontSize: '13px',
            color: (row.pendingCount ?? 0) > 0 ? TEXT : TEXT_DIM,
          }}
        >
          {row.pendingCount ?? 0}
        </div>
      ),
      mobile: 'sheet',
    },
    {
      key: 'failedCount',
      label: 'Failed Sync',
      width: 90,
      minWidth: 84,
      align: 'right',
      sortable: true,
      value: (row) => String(row.failedCount ?? 0),
      render: (row) => (
        <div
          className="font-inter"
          style={{
            fontSize: '13px',
            fontWeight: (row.failedCount ?? 0) > 0 ? 600 : 400,
            color:
              (row.failedCount ?? 0) > 0 ? HEALTH_STYLE.attention.fg : TEXT_DIM,
          }}
        >
          {row.failedCount ?? 0}
        </div>
      ),
      mobile: 'sheet',
    },
    {
      key: 'appVersion',
      label: 'App Version',
      width: 96,
      minWidth: 84,
      value: (row) => row.appVersion ?? '-',
      mobile: 'sheet',
    },
  ];

  const rowActions: ListRowAction<DeviceHealthRow>[] = [
    {
      key: 'details',
      label: 'View Details',
      sheetIcon: 'chev',
      onRun: (row) => setSelected(row),
    },
  ];

  /* ---------------- Detail sheet ---------------- */

  const SheetRow = ({
    label,
    value,
  }: {
    label: string;
    value: React.ReactNode;
  }) => (
    <div
      className="flex items-center justify-between"
      style={{
        padding: '9px 0',
        borderBottom: `1px solid ${BORDER_SOFT}`,
        gap: '16px',
      }}
    >
      <span
        className="font-inter"
        style={{ fontSize: '13px', color: TEXT_MUTED, flex: 'none' }}
      >
        {label}
      </span>
      <span
        className="font-inter"
        style={{
          fontSize: '14px',
          fontWeight: 500,
          color: TEXT,
          textAlign: 'right',
        }}
      >
        {value}
      </span>
    </div>
  );

  const detailSheet = selected && (
    <BottomSheet
      open={selected !== null}
      title={deviceLabelOf(selected)}
      onClose={() => setSelected(null)}
    >
      <div style={{ padding: '2px 0 10px' }}>
        <div
          className="flex items-center"
          style={{ gap: '8px', marginBottom: '6px' }}
        >
          <HealthPill status={healthOf(selected)} language={language} />
          {reasonsOf(selected, language) && (
            <span
              className="font-inter"
              style={{ fontSize: '13px', color: TEXT_MUTED }}
            >
              {reasonsOf(selected, language)}
            </span>
          )}
        </div>
        <SheetRow
          label={t(language, 'Serial Number', 'Serial Number')}
          value={selected.device?.serialNo ?? '-'}
        />
        <SheetRow
          label={t(language, 'Company', 'Company')}
          value={selected.company?.companyName ?? '-'}
        />
        <SheetRow
          label={t(language, 'Location', 'Location')}
          value={selected.location?.name ?? '-'}
        />
        <SheetRow
          label={t(language, 'Last Seen', 'Last Seen')}
          value={`${formatAgo(lastSeenOf(selected), language)} · ${formatExact(lastSeenOf(selected))}`}
        />
        <SheetRow
          label={t(language, 'Network', 'Network')}
          value={selected.networkStatus ?? '-'}
        />
        <SheetRow
          label={t(language, 'Printer Status', 'Printer Status')}
          value={
            selected.printerConnected === null ||
            selected.printerConnected === undefined
              ? '-'
              : selected.printerConnected
                ? t(language, 'Connected', 'Connected')
                : t(language, 'Disconnected', 'Disconnected')
          }
        />
        <SheetRow
          label={t(language, 'Pending Sync', 'Pending Sync')}
          value={selected.pendingCount ?? 0}
        />
        <SheetRow
          label={t(language, 'Failed Sync', 'Failed Sync')}
          value={selected.failedCount ?? 0}
        />
        <SheetRow
          label={t(language, 'Failed Refunds', 'Failed Refunds')}
          value={selected.failedDependencyCount ?? 0}
        />
        <SheetRow
          label={t(language, 'Oldest Unsent Item', 'Oldest Unsent Item')}
          value={formatQueueAge(selected.oldestQueueItemSeconds)}
        />
        <SheetRow
          label={t(language, 'App Version', 'App Version')}
          value={selected.appVersion ?? '-'}
        />
        {selected.lastError && (
          <div style={{ marginTop: '10px' }}>
            <p
              className="font-inter"
              style={{ fontSize: '13px', color: TEXT_MUTED }}
            >
              {t(language, 'Last Error', 'Last Error')}
            </p>
            <pre
              style={{
                marginTop: '6px',
                padding: '10px 12px',
                borderRadius: '10px',
                backgroundColor: '#F7F9F9',
                border: `1px solid ${BORDER_SOFT}`,
                fontSize: '12px',
                lineHeight: '1.5',
                color: TEXT,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: '160px',
                overflowY: 'auto',
              }}
            >
              {selected.lastError}
            </pre>
          </div>
        )}
      </div>
    </BottomSheet>
  );

  return (
    <>
      <div
        className="h-full relative"
        style={{
          overflowY: isMobile ? 'auto' : undefined,
          paddingBottom: isMobile ? '24px' : undefined,
          display: isMobile ? undefined : 'flex',
          flexDirection: isMobile ? undefined : 'column',
        }}
      >
        <div className="lg:flex items-center justify-between">
          <p
            style={{ color: TEXT }}
            className="text-[24px] leading-[130%] font-medium font-inter"
          >
            {t(language, 'Device Health', 'Device Health')}
          </p>
        </div>
        {summaryStrip}

        <ListScreen
          ref={listRef}
          language={language}
          columns={columns}
          fetchRows={fetchHealth}
          search={false}
          filters={filters}
          rowActions={rowActions}
          countNoun="devices"
          emptyState={inforTable}
        />
      </div>
      {detailSheet}
    </>
  );
};

export default DeviceHealth;
